import { Alerts } from 'constants/alerts';
import {
  EXPORT_TYPE_NAMES,
  ExportJobTypes,
  ExportTransportTypes,
  ExportType,
  ExportTypes,
  ReportNames,
} from 'constants/exportTypes';

import React, { useMemo, useState } from 'react';

import { useApiContext } from 'context/apiContext';
import { useReportExport } from 'hooks/queries/useReportExport';
import { ListContextProvider, useList, ResourceContextProvider, useNotify } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { EmptyListAlert } from 'shared/mui/Alert/EmptyListAlert/EmptyListAlert';
import { ExportTypesButton } from 'shared/mui/Buttons/ExportTypesButton/ExportTypesButton';
import { DateRangeValue } from 'shared/mui/DatePicker/DateRangeTimezonePicker/DateRangeTimezonePicker';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { PageHeader } from 'shared/mui/PageHeader';
import { Stack } from 'shared/mui/Stack/Stack';
import { Typography } from 'shared/mui/Typography';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { ExportEditButton } from 'shared/react-admin/Export/ExportEditButton/ExportEditButton';
import { ReportExportDialog } from 'shared/widgets/ReportExportDialog/ReportExportDialog';
import { cleanEmpty } from 'utils/cleanEmpty';

import { EXPORT_FIELDS_EXPENSE_REPORT } from './constants/exportFields';
import { ExpenseReportListAccessAlert } from './ExpenseReportListAccessAlert';
import { ExpenseReportListDatagrid } from './ExpenseReportListDatagrid/ExpenseReportListDatagrid';
import {
  ExpenseReportFilterFormValues,
  ExpenseReportListFilter,
} from './ExpenseReportListFilter/ExpenseReportListFilter';
import { ExpenseReportListFilterChips } from './ExpenseReportListFilter/ExpenseReportListFilterChips';
import { ExpenseReportListSuccessAlert } from './ExpenseReportListSuccessAlert';
import { useExpenseReport } from './hooks/useExpenseReport';
import { buildExpenseReportTableRows } from './utils/buildExpenseReportTableRows';
import { mapFiltersToParams } from './utils/mapFiltersToParams';

const DEFAULT_EXPENSE_REPORT_FILTER_VALUES: ExpenseReportFilterFormValues = {
  appliedDate: [null, null] as DateRangeValue,
  timezone: null,
  currency: '',
  referral: [],
  expenseTypes: [],
  expenseArticles: [],
  expenseSubarticles: [],
};

export const ExpenseReportList = () => {
  const notify = useNotify();

  const { form, appliedFilters, onSubmit, onReset, setAppliedFilter, openDrawer, toggleDrawer } =
    useFilters<ExpenseReportFilterFormValues>({
      mode: 'all',
      defaultValues: DEFAULT_EXPENSE_REPORT_FILTER_VALUES,
      resetValues: DEFAULT_EXPENSE_REPORT_FILTER_VALUES,
    });

  const { user } = useApiContext();
  const isShowAccessAlert = user.alerts.includes(Alerts.EmptyProviderAccountAccess);

  const filterParams = useMemo(
    () => mapFiltersToParams(appliedFilters as ExpenseReportFilterFormValues),
    [appliedFilters],
  );
  const { expenseReport, isLoading, isFetching } = useExpenseReport(filterParams);
  const flattenedExpenseReport = expenseReport ? buildExpenseReportTableRows(expenseReport) : [];

  const expenseReportContext = useList({
    data: flattenedExpenseReport,
  });

  const [exportType, setExportType] = useState<ExportType>(ExportTypes.Excel);
  const [isOpenExportDialog, setIsOpenExportDialog] = useState(false);
  const [isSendReportOnEmail, setIsSendReportOnEmail] = useState(false);

  const { mutate: exportReportMutate, isLoading: isExportLoading } = useReportExport({
    filters: cleanEmpty({
      ...filterParams,
      exportType: exportType,
      jobType: ExportJobTypes.ExportExpenseReport,
      ...(isSendReportOnEmail && { notificationTransport: [ExportTransportTypes.Email] }),
    }),
    preferenceKey: ReportNames.ExpenseReport,
    onSuccess: () => {
      notify(
        `Файл экспорта отчёта по расходам формируется ${isSendReportOnEmail ? 'и будет отправлен вам на почту' : ''}`,
        {
          type: 'info',
        },
      );
      setIsOpenExportDialog(false);
    },
    onError: (error) => {
      notify(error.data?.errors[0]?.title, { type: 'error' });
    },
  });

  const isListFilterApplied =
    Object.keys(filterParams).length > 0 && form.formState.isDirty && form.formState.isSubmitted;

  return (
    <ResourceContextProvider value="expense_report">
      <ListContextProvider value={expenseReportContext}>
        <PageHeader
          actions={[
            <ExportTypesButton
              disabled={!isListFilterApplied}
              exportType={exportType}
              key="export-button"
              onChangeType={setExportType}
              onClick={() => setIsOpenExportDialog(true)}
            />,
          ]}
          headerTitle={{
            titleText: 'Отчёт по расходам',
          }}
          mt={3}
        />
        <FilterToolbar
          leftActionsSlot={[
            <FilterButton key="filter" onClick={toggleDrawer(true)} />,
            <FilterColumnsButton disabled={!flattenedExpenseReport?.length} key="select-column" />,
          ]}
        />
        <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
          <FormProvider {...form}>
            <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
              <FilterDrawerContent>
                <ExpenseReportListFilter />
              </FilterDrawerContent>
              <FilterDrawerActionsButtons>
                <FilterResetButton label="Очистить" onReset={onReset} />
                <FilterApplyButton disabled={!form.formState.isValid} type="submit" />
              </FilterDrawerActionsButtons>
            </form>
          </FormProvider>
        </FilterDrawer>
        <FilterChipsToolbar appliedFilters={appliedFilters} isSkipBooleanValues={false}>
          <FilterContextProvider
            appliedFilters={appliedFilters}
            setAppliedFilter={setAppliedFilter}
          >
            <FormProvider {...form}>
              <ExpenseReportListFilterChips />
              <FilterClearButton
                key="clear"
                onClick={() => {
                  onReset();
                }}
                visible={isListFilterApplied}
              />
            </FormProvider>
          </FilterContextProvider>
        </FilterChipsToolbar>
        {isShowAccessAlert && (
          <ExpenseReportListAccessAlert firstName={user.firstName} lastName={user.lastName} />
        )}
        {!isShowAccessAlert && !expenseReport && !isListFilterApplied && (
          <EmptyListAlert onClick={toggleDrawer(true)} />
        )}
        {!isShowAccessAlert && isLoading && <DatagridSkeleton columnsCount={12} />}
        {!isShowAccessAlert && expenseReport && (
          <>
            <Stack direction="column" mb={2}>
              {expenseReport?.referrals?.length && (
                <ExpenseReportListSuccessAlert
                  currency={filterParams.currency as string}
                  dateFrom={filterParams.dateFrom as number}
                  dateTo={filterParams.dateTo as number}
                />
              )}
            </Stack>
            <ExpenseReportListDatagrid isPending={isFetching} />
          </>
        )}
        <ReportExportDialog
          exportType={EXPORT_TYPE_NAMES[exportType]}
          isLoading={isExportLoading || expenseReportContext.isLoading}
          onChange={() => setIsSendReportOnEmail((prev) => !prev)}
          onClose={() => setIsOpenExportDialog(false)}
          onSubmit={exportReportMutate}
          open={isOpenExportDialog}
          sendOnEmail={isSendReportOnEmail}
          title={
            <>
              <Typography component="span" sx={{ verticalAlign: 'middle' }} variant="inherit">
                Экспорт отчёта
              </Typography>
              <ExportEditButton
                fields={EXPORT_FIELDS_EXPENSE_REPORT}
                preferenceKey={ReportNames.ExpenseReport}
              />
            </>
          }
          userEmail={user?.corporateEmail || user?.email}
        />
      </ListContextProvider>
    </ResourceContextProvider>
  );
};
